import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import deepEqual from "fast-deep-equal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { transformWorkHistoryToFormData } from "../utils/workHistoryTransform";
import { transformToRailsParams } from "../utils/railsParamsTransform";
import {
  getDefaultFormValues,
  getInitialMasterData,
} from "../config/defaultValues";
import {
  fetchMasterData,
  fetchFrameworkData,
  fetchBusinessSystemProductLanguageData,
  fetchCloudServiceProductData,
  fetchServerEnvironmentData,
  fetchMiddlewareProductData,
} from "../utils/apiClient";
import {
  targetFromContext,
  memberSkillSheetPathFor,
} from "../../utils/skillSheetRouting";
import { normalizeForDirty } from "../../utils/normalizeForDirty";
import { useDirtyState } from "../../shared/hooks/useDirtyState";
import { useDemoDraft } from "../hooks/useDemoDraft";

const SkillSheetMainStepperContext = createContext();

/**
 * SkillSheetMainStepperのコンテキストフック
 * @returns {Object} コンテキストオブジェクト
 */
export const useSkillSheetMainStepper = () => {
  const context = useContext(SkillSheetMainStepperContext);
  if (!context) {
    throw new Error(
      "useSkillSheetMainStepper must be used within a SkillSheetMainStepperProvider",
    );
  }
  return context;
};

/**
 * SkillSheetMainStepperのプロバイダーコンポーネント
 * @param {Object} props - プロパティ
 * @param {React.ReactNode} props.children - 子コンポーネント
 * @param {string} props.csrfToken - CSRFトークン
 * @param {string} props.formAction - フォームアクション
 * @param {Object|null} props.workHistory - 既存の作業履歴（編集モード時）
 */
export const SkillSheetMainStepperProvider = ({
  children,
  csrfToken,
  formAction,
  workHistory = null,
  targetUserId = null,
  targetUserType = null,
  demo = false,
}) => {
  const navigate = useNavigate();

  // create → edit へ切り替えるために state 化
  const [action, setAction] = useState(formAction);
  const [workHistoryState, setWorkHistoryState] = useState(workHistory); // 初期はnullでもOK
  const [httpMethod, setHttpMethod] = useState(workHistory ? "PATCH" : "POST");
  const isEditMode = httpMethod === "PATCH";

  // 二重送信ガード
  const inFlightRef = useRef(false);

  // dirty 管理
  const dirtyRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  useDirtyState(dirty);

  // react-hook-formを使用してフォーム状態を管理
  const methods = useForm({
    mode: "onChange",
    defaultValues: getDefaultFormValues(),
  });

  // 初期値（正規化済み）のスナップショット
  const initialSnapshotRef = useRef(null);

  // watchで値を購読（フォーム全体）
  const watchedValues = methods.watch();

  useEffect(() => {
    // 初回のみ：現在のフォーム値を「初期値」として保存（dirty判定の基準）
    if (!initialSnapshotRef.current) {
      initialSnapshotRef.current = normalizeForDirty(methods.getValues());
      return;
    }

    // 現在のフォーム値を正規化して比較（stringifyしない）
    const initial = initialSnapshotRef.current;
    const current = normalizeForDirty(watchedValues);
    const nextDirty = !deepEqual(initial, current);

    // dirty状態が変わらない場合は何もしない
    if (dirtyRef.current === nextDirty) return;

    // dirty状態を更新し、Layout に通知
    dirtyRef.current = nextDirty;
    setDirty(nextDirty);
  }, [watchedValues, methods]);

  // デモの下書きを復元したときに呼ばれる。復元した内容を「初期値」として
  // 記録し直し、まだ何も編集していない扱いにする（dirty の誤判定を防ぐ）
  const handleDemoRestore = useCallback(() => {
    initialSnapshotRef.current = normalizeForDirty(methods.getValues());
    dirtyRef.current = false;
    setDirty(false);
  }, [methods]);

  // デモ：入力内容の localStorage 保存 / 復元 / エラー通知（demo=false では何もしない）
  const {
    flushDemoDraft,
    clearDemoForm,
    demoStorageWarning,
    dismissDemoStorageWarning,
  } = useDemoDraft({
    demo,
    methods,
    watchedValues,
    dirty,
    onRestore: handleDemoRestore,
  });

  // 状態管理
  const [masterData, setMasterData] = useState(getInitialMasterData());
  const [stepValidators, setStepValidators] = useState({});

  // props 変更時の対応
  useEffect(() => {
    setAction(formAction);
  }, [formAction]);

  useEffect(() => {
    setWorkHistoryState(workHistory);
    setHttpMethod(workHistory ? "PATCH" : "POST");
  }, [workHistory]);

  // 編集モードの場合、workHistoryからフォームデータを初期化
  useEffect(() => {
    if (isEditMode && workHistoryState) {
      const initialFormData = transformWorkHistoryToFormData(workHistoryState);

      // 参画中の場合、終了日を空文字（UI上で空表示）
      if (workHistoryState.participation_flag) {
        initialFormData.period_at = "";
      }

      methods.reset(initialFormData);

      initialSnapshotRef.current = normalizeForDirty(methods.getValues());
      dirtyRef.current = false;
      setDirty(false);
    }
  }, [workHistoryState, methods, isEditMode]);

  // ステップバリデーション関数
  const validateStep = useCallback((stepIndex, validatorFunction) => {
    setStepValidators((prev) => ({
      ...prev,
      [stepIndex]: validatorFunction,
    }));
  }, []);

  const runStepValidation = useCallback(
    (stepIndex) => {
      // デモモード（TOPページ埋め込み）は入力チェックを行わず、自由に閲覧させる
      if (demo) return true;

      const validator = stepValidators[stepIndex];
      if (validator && typeof validator === "function") {
        return validator();
      }
      return true;
    },
    [stepValidators, demo],
  );

  // マスタデータ取得関数をコンテキストにマップ
  const contextFetchMasterData = useCallback(async (dataType) => {
    const data = await fetchMasterData(dataType);
    setMasterData((prev) => ({
      ...prev,
      [dataType]: data,
    }));
  }, []);

  // 保存成功反映
  const applySaveSuccess = useCallback(
    (data) => {
      const nextAction = data?.form_action || data?.edit_form_action;
      const nextMethod = data?.http_method || "PATCH";
      const returnedWorkHistory = data?.work_history || data?.workHistory;

      if (nextAction) setAction(nextAction);
      setHttpMethod(nextMethod);

      // サーバーの最新work_historyをstateに反映
      if (returnedWorkHistory) {
        setWorkHistoryState(returnedWorkHistory);
        return;
      }

      // work_history本体が無い場合はidだけ更新
      if (data?.work_history_id) {
        setWorkHistoryState((prev) => ({
          ...(prev || {}),
          id: data.work_history_id,
        }));
      }
    },
    [setAction, setHttpMethod, setWorkHistoryState],
  );

  // フォーム送信処理
  const submitForm = useCallback(
    async ({ navigateOnSuccess = true, successMessage } = {}) => {
      // デモモード（TOPページ埋め込み）では永続化しない
      if (demo) return;

      if (inFlightRef.current) return;

      inFlightRef.current = true;

      try {
        const formData = methods.getValues();
        const railsParams = transformToRailsParams(formData, workHistoryState);

        const res = await fetch(action, {
          method: httpMethod,
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            Accept: "application/json",
          },
          body: JSON.stringify(railsParams),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          const errorMessage =
            data.message ||
            data.error ||
            (Array.isArray(data.errors) ? data.errors.join("\n") : null) ||
            "保存に失敗しました";
          toast.error(errorMessage);
          return;
        }

        if (data.status === "ok") {
          if (successMessage) toast.success(successMessage);

          // Sidebar の参画プロジェクトを再取得させる
          window.dispatchEvent(new Event("app:refreshSidebarMenus"));

          // 保存成功したので dirty をクリア
          dirtyRef.current = false;
          setDirty(false);

          // 現在の値を「初期値」として再セット（isDirtyをfalseにする）
          methods.reset(methods.getValues());

          // 現在値を新しい初期値としてスナップショット更新
          initialSnapshotRef.current = normalizeForDirty(methods.getValues());

          applySaveSuccess(data);

          // 一時保存の場合は遷移しない
          if (navigateOnSuccess) {
            const owner = targetFromContext({ targetUserId, targetUserType });
            navigate(memberSkillSheetPathFor(owner));
          }

          return;
        }

        toast.error(data.message || "保存に失敗しました");
      } catch (error) {
        toast.error(
          "通信エラーが発生しました。ネットワーク環境をご確認ください。",
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [
      csrfToken,
      action,
      httpMethod,
      methods,
      workHistoryState,
      navigate,
      targetUserId,
      targetUserType,
      applySaveSuccess,
      demo,
    ],
  );

  // コンテキスト値の構築
  const contextValue = {
    ...methods,
    masterData,
    fetchMasterData: contextFetchMasterData,
    fetchFrameworkData,
    fetchBusinessSystemProductLanguageData,
    fetchCloudServiceProductData,
    fetchServerEnvironmentData,
    fetchMiddlewareProductData,
    submitForm,
    validateStep,
    runStepValidation,
    isEditMode,
    workHistory: workHistoryState,
    targetUserId,
    targetUserType,
    demo,
    clearDemoForm,
    flushDemoDraft,
    demoStorageWarning,
    dismissDemoStorageWarning,
  };

  return (
    <SkillSheetMainStepperContext.Provider value={contextValue}>
      {children}
    </SkillSheetMainStepperContext.Provider>
  );
};

/**
 * フォーム要素を作成する
 * @param {Object} railsParams - Railsパラメータ
 * @param {string} csrfToken - CSRFトークン
 * @param {string} formAction - フォームアクション
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {HTMLFormElement} 作成されたフォーム要素
 */
const createFormElement = (railsParams, csrfToken, formAction, isEditMode) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = formAction;

  // 編集モードの場合はPATCHメソッドを追加
  if (isEditMode) {
    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "patch";
    form.appendChild(methodInput);
  }

  // CSRF トークン
  const csrfInput = document.createElement("input");
  csrfInput.type = "hidden";
  csrfInput.name = "authenticity_token";
  csrfInput.value = csrfToken;
  form.appendChild(csrfInput);

  // URLパラメータから user_id / company_user_id を拾って hidden input に追加する
  const urlParams = new URLSearchParams(window.location.search);
  const keysToInclude = ["user_id", "company_user_id"];

  keysToInclude.forEach((key) => {
    const paramValue = urlParams.get(key);
    if (paramValue) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = paramValue;
      form.appendChild(input);
    }
  });

  // パラメータを再帰的に追加
  addInputsRecursively(form, railsParams);

  return form;
};

/**
 * オブジェクトを再帰的にフォーム入力として追加する
 * @param {HTMLFormElement} form - フォーム要素
 * @param {Object} obj - 追加するオブジェクト
 * @param {string} prefix - プレフィックス
 */
const addInputsRecursively = (form, obj, prefix = "") => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const name = prefix ? `${prefix}[${key}]` : key;

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      addInputsRecursively(form, value, name);
    } else {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value || "";
      form.appendChild(input);
    }
  });
};
